import React, { useRef, useState } from "react";
import { LoadingSpinner } from "../Loading/Loading";
import Props from "prop-types";
import clsx from "clsx";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import styles from "./Tags.module.scss";
import { useOnClickOutside } from "../utils/useClickOutside";

const TagOption = ({ value, option, defaultValue, optionOnClick }) => {
  const dropdownItemClasses = clsx(styles.dropdownChild, {
    [styles.activeDropdown]: !isEmpty(value)
      ? value?.value === option?.value
      : defaultValue?.value === option?.value,
  });
  return (
    <div
      className={dropdownItemClasses}
      id={option?.value}
      value={option?.value}
      onClick={(e) => {
        optionOnClick(e, option);
      }}
    >
      {option?.label}
    </div>
  );
};

const LabelText = ({ loading, label }) => {
  return loading ? <LoadingSpinner size="ExtraSmall" /> : label;
};

const Tag = ({
  color,
  className,
  label,
  loading,
  disabled,
  options,
  onClick,
  onChange,
  defaultValue,
  value,
  position,
  onClickOnTagButtonMenu,
}) => {
  const ref = useRef();

  const [menuDropDown, setMenuDropDown] = useState(false);

  useOnClickOutside(ref, () => {
    setMenuDropDown(false);
  });

  const stringLabelFormatter = (label) => label && String(label).slice(0, 2).toLowerCase();

  // check if the props value exists in the options
  const findValue = (pasedValue, passedOptions) => {
    if (!isEmpty(options)) {
      const foundValue = passedOptions.find((option) => option.value === pasedValue);
      return foundValue ? foundValue : null;
    }
    return null;
  };

  const lowerCase = (string) => String(string).toLocaleLowerCase();

  const isValid =
    lowerCase(value?.name) === lowerCase(label) ||
    lowerCase(defaultValue?.name) === lowerCase(label);

  const tagClasses = clsx(styles.tag, {
    [styles.disabled]: disabled,
    [styles.primary]: color === "primary" && !isValid,
    [styles.error]: color === "error" && !isValid,
    [styles["active-primary"]]: isValid && !disabled && color === "primary",
    [styles["active-error"]]: isValid && !disabled && color === "error",
    [styles.isLoading]: loading,
    className,
  });

  const iconClasses = clsx(styles.menuButton, {
    [styles.positionLeft]: position === "left",
    [styles.positionRight]: position === "right",
  });

  // toggling click of tag
  const onClickHandler = (e) => {
    e.stopPropagation();
    const dropDownValue =
      !isEmpty(options) &&
      (findValue(defaultValue?.value, options) || findValue(options[0]?.value, options));

    if (!disabled && !loading) {
      if (isEmpty(options)) {
        const valueName = value?.name || label;

        onClick({ name: valueName });
      } else {
        onClick({
          name: label,
          value: value?.value ? null : dropDownValue?.value,
        });
      }
    }
  };

  const onClickOnTagButtonMenuHandler = (e) => {
    e.stopPropagation();
    const dropDownValue =
      (!isEmpty(options) && findValue(value?.value, options)) ||
      findValue(defaultValue?.value, options);
    onClickOnTagButtonMenu({
      name: label,
      value: dropDownValue?.value,
    });
    setMenuDropDown(!menuDropDown);
  };

  // handling option onchange
  const optionOnClick = (e, dropdown) => {
    e.stopPropagation();
    if (!disabled && !loading) {
      onChange({ name: label, value: dropdown.value });
      setMenuDropDown(false);
    }
  };

  const labelOfButtonTag =
    !isEmpty(options) &&
    stringLabelFormatter(
      findValue(value?.value, options)?.label || findValue(defaultValue?.value, options)?.label
    );

  return (
    <button disabled={disabled} className={tagClasses} onClick={onClickHandler}>
      {position === "right" && <LabelText loading={loading} label={label} />}
      {!isEmpty(options) && (
        <div
          role="button"
          ref={ref}
          id={label}
          onClick={onClickOnTagButtonMenuHandler}
          className={iconClasses}
          data-testid={`tag-options-button-${label.toLocaleLowerCase()}`}
        >
          {labelOfButtonTag || ""}
          {menuDropDown && !disabled && (
            <div
              className={styles.dropDownContainer}
              data-testid={`tag-options-${label.toLocaleLowerCase()}`}
            >
              {!isEmpty(options) &&
                options.map((option, index) => (
                  <TagOption
                    key={index}
                    optionOnClick={optionOnClick}
                    value={value}
                    option={option}
                    defaultValue={defaultValue}
                  />
                ))}
            </div>
          )}
        </div>
      )}
      {position === "left" && <LabelText loading={loading} label={label} />}
    </button>
  );
};

const Tags = ({
  containerClassNames,
  tagClassNames,
  color,
  tags,
  options,
  defaultOptionValue,
  value,
  onClick,
  loading,
  onChange,
  disabled,
  max,
}) => {
  const containerClasses = clsx("d-flex flex-wrap", containerClassNames);

  const handleTagOnClick = (tagValue) => {
    let tagValues = [];

    if (!isEmpty(options)) {
      const isExisted = find(value, (val) => val?.name === tagValue?.name);
      if (isExisted) {
        const filteredValue = value.filter((val) => val?.name !== isExisted.name);
        tagValues = filteredValue;
      } else {
        tagValues = [...value, tagValue];
      }
    } else {
      const isExisted = find(value, (val) => val?.name === tagValue?.name);
      if (!isExisted) {
        tagValues = [...value, { name: tagValue.name }];
      } else {
        const filteredValue = value.filter((val) => val?.name !== isExisted.name);
        tagValues = filteredValue;
      }
    }

    if (typeof onClick === "function") {
      onClick(tagValues);
    }
  };

  const handleTagOnChange = (tagValue) => {
    let tagValues = [];

    if (!isEmpty(options)) {
      const isExisted = find(value, (val) => val.name === tagValue.name);

      if (isExisted) {
        tagValues = value.map((val) => {
          if (val.name === tagValue.name) {
            return tagValue;
          }
          return val;
        });
      } else {
        tagValues = [...value, tagValue];
      }
    } else {
      tagValues = [...value, tagValue];
    }

    if (typeof onClick === "function") {
      onClick(tagValues);
    }

    if (typeof onChange === "function") {
      onChange(tagValues);
    }
  };

  return (
    <div className={containerClasses}>
      {tags &&
        tags.map((tag, index) => {
          const tagValue = value?.find((val) => val?.name === tag?.name);

          if (max && index >= max) {
            return null;
          }

          return (
            <Tag
              color={tag?.color || color}
              key={index}
              disabled={tag?.disabled || disabled}
              className={tagClassNames}
              options={options}
              label={tag?.name}
              loading={loading}
              position={tag?.position}
              value={tagValue}
              defaultValue={defaultOptionValue}
              onClick={(value) => {
                if ((!tag?.disabled || !disabled) && !loading) {
                  handleTagOnClick(value);
                }
              }}
              onChange={(value) => {
                if ((!tag?.disabled || !disabled) && !loading) {
                  handleTagOnChange(value);
                }
              }}
              onClickOnTagButtonMenu={handleTagOnChange}
            />
          );
        })}
    </div>
  );
};

Tags.defaultProps = {
  color: "primary",
  value: [],
};

Tags.propTypes = {
  color: Props.oneOf(["primary", "secondary", "success", "warning", "error"]).isRequired,
  tags: Props.arrayOf(
    Props.shape({
      name: Props.string.isRequired,
      position: Props.oneOf(["left", "right"]),
      color: Props.oneOf(["primary", "secondary", "success", "warning", "error"]),
      disabled: Props.bool,
    })
  ),
  loading: Props.bool,
  disabled: Props.bool,
  options: Props.arrayOf(
    Props.shape({
      value: Props.string.isRequired,
      label: Props.string.isRequired,
    })
  ),
  value: Props.arrayOf(
    Props.shape({
      name: Props.string,
      value: Props.string,
    })
  ),
  defaultOptionValue: Props.shape({
    name: Props.string,
    value: Props.string,
  }),
  onClick: Props.func,
  onChange: Props.func,
  isActive: Props.bool,
  max: Props.number,
};

Tag.defaultProps = {
  color: "primary",
  isActive: false,
  position: "right",
  loading: false,
};

Tag.propTypes = {
  color: Props.oneOf(["primary", "error"]),
  label: Props.string.isRequired,
  loading: Props.bool,
  disabled: Props.bool,
  options: Props.arrayOf(
    Props.shape({
      value: Props.string.isRequired,
      label: Props.string.isRequired,
    })
  ),
  value: Props.shape({
    name: Props.string,
    value: Props.string,
  }),
  defaultValue: Props.shape({
    name: Props.string,
    value: Props.string,
  }),
  onClick: Props.func,
  onChange: Props.func,
  isActive: Props.bool,
  position: Props.oneOf(["left", "right"]),
};

export default Tags;
